import { useRef } from 'react'
import { NetworkStatus } from '@apollo/client'
import { StoredFilterParams } from '@equipmentshare/dcf-search-filter-experience'
import { useTransferOrdersQuery, TransferOrdersQuery, TransferOrderStatus, TransferOrderType } from '../../../graphql'
import { CancelTransferOrderPermission, resolveCancelPermission, useCancelGrants } from './useCanCancelTransferOrder'

type TransferOrderNode = NonNullable<TransferOrdersQuery['transferOrders']['edges'][number]['node']>

export type TransferOrderRow = TransferOrderNode & {
	/**
	 * Resolved here, once for the page, rather than by each row's actions menu:
	 * a per-row permissions query would be one request per row.
	 */
	cancelPermission: CancelTransferOrderPermission,
}

export type TransferOrdersSearchRequest = {
	search?: string,
	statuses?: string,
	fromInventoryIds?: string,
	toInventoryIds?: string,
	orderTypes?: string,
	isClosed?: string,
	fulfillmentOrderNumbers?: string,
	page?: number,
	size?: number,
}

export default function useTransferOrdersDataQuery(
	apiFilterParams: StoredFilterParams<TransferOrdersSearchRequest>
) {
	const size = apiFilterParams.size
		? Number( Array.isArray( apiFilterParams.size ) ? apiFilterParams.size[ 0 ] : apiFilterParams.size )
		: 25
	const page = apiFilterParams.page
		? Number( Array.isArray( apiFilterParams.page ) ? apiFilterParams.page[ 0 ] : apiFilterParams.page )
		: 0
	const search = Array.isArray( apiFilterParams.search ) ? apiFilterParams.search[ 0 ] : apiFilterParams.search

	const statusesRaw = apiFilterParams.statuses
	const statuses = Array.isArray( statusesRaw )
		? statusesRaw as TransferOrderStatus[]
		: statusesRaw ? [ statusesRaw as TransferOrderStatus ] : undefined

	const orderTypesRaw = apiFilterParams.orderTypes
	const orderTypes = Array.isArray( orderTypesRaw )
		? orderTypesRaw as TransferOrderType[]
		: orderTypesRaw ? [ orderTypesRaw as TransferOrderType ] : undefined

	const fromInventoryIdsRaw = apiFilterParams.fromInventoryIds
	const fromInventoryIds = Array.isArray( fromInventoryIdsRaw )
		? fromInventoryIdsRaw
		: fromInventoryIdsRaw ? [ fromInventoryIdsRaw ] : undefined

	const toInventoryIdsRaw = apiFilterParams.toInventoryIds
	const toInventoryIds = Array.isArray( toInventoryIdsRaw )
		? toInventoryIdsRaw
		: toInventoryIdsRaw ? [ toInventoryIdsRaw ] : undefined

	const isClosedRaw = apiFilterParams.isClosed
	const isClosedStr = Array.isArray( isClosedRaw ) ? isClosedRaw[ 0 ] : isClosedRaw
	const isClosed = isClosedStr === 'true' ? true : isClosedStr === 'false' ? false : undefined

	const fulfillmentOrderNumbersRaw = apiFilterParams.fulfillmentOrderNumbers
	const fulfillmentOrderNumbers = Array.isArray( fulfillmentOrderNumbersRaw )
		? fulfillmentOrderNumbersRaw
		: fulfillmentOrderNumbersRaw ? [ fulfillmentOrderNumbersRaw ] : undefined

	// Track end cursors indexed by page number so forward pagination works.
	// Reset the map whenever filters, search, or page size change.
	const cursorsRef = useRef<( string | undefined )[]>( [ undefined ] )
	const prevSizeRef = useRef( size )
	const prevFiltersRef = useRef( JSON.stringify( { search, statusesRaw, orderTypesRaw, fromInventoryIdsRaw, toInventoryIdsRaw, isClosedRaw, fulfillmentOrderNumbersRaw } ) )

	const filtersKey = JSON.stringify( { search, statusesRaw, orderTypesRaw, fromInventoryIdsRaw, toInventoryIdsRaw, isClosedRaw, fulfillmentOrderNumbersRaw } )

	if (
		prevSizeRef.current !== size ||
		prevFiltersRef.current !== filtersKey
	) {
		cursorsRef.current = [ undefined ]
		prevSizeRef.current = size
		prevFiltersRef.current = filtersKey
	}

	const { data, previousData, networkStatus } = useTransferOrdersQuery( {
		variables: {
			search,
			statuses,
			orderTypes,
			fromInventoryIds,
			toInventoryIds,
			isClosed,
			fulfillmentOrderNumbers,
			paging: {
				first: size,
				after: cursorsRef.current[ page ],
			},
		},
		fetchPolicy: 'network-only',
		notifyOnNetworkStatusChange: true,
	} )

	const displayData = data ?? previousData

	if ( displayData?.transferOrders.pageInfo.endCursor ) {
		cursorsRef.current[ page + 1 ] = displayData.transferOrders.pageInfo.endCursor
	}

	const nodes = displayData?.transferOrders.edges.map( ( edge ) => edge.node ) ?? []

	// Every store on the page in one filtered request.
	const { granted } = useCancelGrants( nodes.flatMap( ( node ) => [ node.fromStore?.id, node.toStore?.id ] ) )

	const rows: TransferOrderRow[] = nodes.map( ( node ) => ( {
		...node,
		cancelPermission: resolveCancelPermission( granted, node.fromStore?.id, node.toStore?.id ),
	} ) )

	return {
		isFetching: networkStatus !== NetworkStatus.ready,
		data: displayData?.transferOrders
			? {
				rows,
				rowCount: displayData.transferOrders.totalCount,
			}
			: undefined,
	}
}
